import { formatTableCell, type TableModel } from './table-model';

/**
 * Export da ficha de metadados. Diferente da tabela de dados, aqui o destino é leitura
 * humana — daí PDF e TXT em vez de planilha.
 *
 * O modelo é o mesmo `TableModel` de duas colunas (campo/valor) usado no resto do app.
 */

function pairs(model: TableModel): { label: string; value: string }[] {
  const [labelColumn, valueColumn] = model.columns;
  if (!labelColumn || !valueColumn) return [];
  return model.rows.map((row) => ({
    label: formatTableCell(labelColumn, row),
    value: formatTableCell(valueColumn, row),
  }));
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function withExtension(fileName: string, extension: string): string {
  const base = fileName || 'metadados';
  return base.toLowerCase().endsWith(`.${extension}`) ? base : `${base}.${extension}`;
}

export function downloadMetadataTxt(model: TableModel, fileName: string, title = ''): boolean {
  if (typeof document === 'undefined') return false;
  const items = pairs(model);
  if (!items.length) return false;

  const linhas: string[] = [];
  if (title) linhas.push(title, '='.repeat(Math.min(title.length, 80)), '');
  for (const { label, value } of items) {
    linhas.push(`${label}: ${value}`);
  }

  // CRLF para abrir certo no Bloco de Notas do Windows.
  downloadBlob(new Blob(['﻿' + linhas.join('\r\n') + '\r\n'], { type: 'text/plain;charset=utf-8' }), withExtension(fileName, 'txt'));
  return true;
}

/** Margens e métrica da página em milímetros (A4 retrato). */
const PAGE = { width: 210, height: 297, margin: 20, lineHeight: 6 };

export async function downloadMetadataPdf(
  model: TableModel,
  fileName: string,
  title = '',
): Promise<boolean> {
  if (typeof document === 'undefined') return false;
  const items = pairs(model);
  if (!items.length) return false;

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const maxWidth = PAGE.width - PAGE.margin * 2;
  let y = PAGE.margin;

  const quebraPagina = (altura: number) => {
    if (y + altura <= PAGE.height - PAGE.margin) return;
    doc.addPage();
    y = PAGE.margin;
  };

  if (title) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    for (const linha of doc.splitTextToSize(title, maxWidth) as string[]) {
      quebraPagina(PAGE.lineHeight + 2);
      doc.text(linha, PAGE.margin, y);
      y += PAGE.lineHeight + 1;
    }
    y += 4;
  }

  for (const { label, value } of items) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    quebraPagina(PAGE.lineHeight);
    doc.text(label.toUpperCase(), PAGE.margin, y);
    y += PAGE.lineHeight - 1;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    for (const linha of doc.splitTextToSize(value || '—', maxWidth) as string[]) {
      quebraPagina(PAGE.lineHeight);
      doc.text(linha, PAGE.margin, y);
      y += PAGE.lineHeight;
    }
    y += 3;
  }

  doc.save(withExtension(fileName, 'pdf'));
  return true;
}
